import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { invokeEdgeFunction } from "@/utils/invokeEdgeFunction";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Upload, FileText, Loader2 } from "lucide-react";
import {
  LateralCandidateForm,
  LateralCandidateFormData,
  emptyLateralCandidateForm,
} from "@/components/lateral/LateralCandidateForm";

export default function CreateLateralCandidatePage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [form, setForm] = useState<LateralCandidateFormData>(emptyLateralCandidateForm());
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUrl, setResumeUrl] = useState<string | null>(null);
  const [resumeMarkdown, setResumeMarkdown] = useState<string | null>(null);
  const [parsedData, setParsedData] = useState<any>(null);
  const [isParsing, setIsParsing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (key: keyof LateralCandidateFormData, value: string) =>
    setForm((f) => ({ ...f, [key]: value }));

  const parseResume = async (file: File) => {
    try {
      setIsParsing(true);

      const formData = new FormData();
      formData.append("file", file);
      const uploadResult = await invokeEdgeFunction<{ url: string }>("upload-resume", {
        body: formData,
      });
      if (!uploadResult.url) throw new Error("Upload failed");
      setResumeUrl(uploadResult.url);

      const parseResult = await invokeEdgeFunction<{
        success: boolean;
        data: any;
        markdown: string | null;
      }>("parse-resume-gemini", {
        body: { fileUrl: uploadResult.url, positionApplied: "" },
      });

      if (parseResult?.success && parseResult?.data) {
        const parsed = parseResult.data;
        const md = parseResult.markdown ?? parsed.resume_markdown ?? null;
        setParsedData(parsed);
        setResumeMarkdown(md);

        setForm((f) => ({
          ...f,
          full_name: parsed.full_name || f.full_name,
          email: parsed.email || f.email,
          phone: parsed.phone || f.phone,
          linkedin_url: parsed.linkedin_url || f.linkedin_url,
          github_url: parsed.github_url || f.github_url,
          key_skills: parsed.key_skills?.join(", ") || f.key_skills,
          technologies: parsed.technologies?.join(", ") || f.technologies,
        }));

        toast({ title: "Resume parsed", description: "Form has been auto-filled. Please review." });
      } else {
        toast({
          title: "Parsing failed",
          description: "Review and fill manually.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsParsing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setResumeFile(file);
    await parseResume(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name.trim()) return;
    setIsSubmitting(true);
    try {
      const toArr = (s: string) =>
        s
          .split(",")
          .map((v) => v.trim())
          .filter(Boolean);

      const { error } = await supabase.from("lateral_candidates").insert({
        full_name: form.full_name,
        email: form.email || null,
        phone: form.phone || null,
        current_company: form.current_company || null,
        current_designation: form.current_designation || null,
        total_experience_years: form.total_experience_years
          ? parseFloat(form.total_experience_years)
          : null,
        relevant_experience_years: form.relevant_experience_years
          ? parseFloat(form.relevant_experience_years)
          : null,
        expected_ctc: form.expected_ctc || null,
        current_ctc: form.current_ctc || null,
        notice_period: form.notice_period || null,
        key_skills: form.key_skills ? toArr(form.key_skills) : null,
        technologies: form.technologies ? toArr(form.technologies) : null,
        linkedin_url: form.linkedin_url || null,
        github_url: form.github_url || null,
        resume_url: resumeUrl,
        resume_markdown: resumeMarkdown,
        parsed_resume_data: parsedData,
        lateral_job_position_id: form.lateral_job_position_id || null,
        notes: form.notes || null,
        status: "applied",
      });

      if (error) throw error;
      toast({ title: "Candidate added" });
      navigate("/admin/lateral/candidates");
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/admin/lateral/candidates")}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Add Lateral Candidate</h1>
          <p className="text-sm text-gray-500">Upload a CV to auto-fill or enter details manually</p>
        </div>
      </div>

      {/* PDF Upload */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Upload Resume (optional)</CardTitle>
        </CardHeader>
        <CardContent>
          <label className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-6 cursor-pointer hover:border-blue-400 transition-colors">
            {isParsing ? (
              <div className="flex items-center gap-2 text-blue-600">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>Parsing resume...</span>
              </div>
            ) : resumeFile ? (
              <div className="flex items-center gap-2 text-green-600">
                <FileText className="h-5 w-5" />
                <span className="text-sm">{resumeFile.name}</span>
              </div>
            ) : (
              <>
                <Upload className="h-8 w-8 text-gray-400 mb-2" />
                <p className="text-sm text-gray-500">Click to upload PDF resume</p>
              </>
            )}
            <input
              type="file"
              accept=".pdf"
              className="hidden"
              onChange={handleFileChange}
              disabled={isParsing}
            />
          </label>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        <LateralCandidateForm values={form} onChange={handleChange} />
        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/admin/lateral/candidates")}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting || isParsing}>
            {isSubmitting ? "Saving..." : "Add Candidate"}
          </Button>
        </div>
      </form>
    </div>
  );
}
